import React from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import { ResponsivePie } from '@nivo/pie';
import { ResponsiveCalendar } from '@nivo/calendar';
import { Empty } from './basics';

const theme = {
  fontFamily: 'inherit',
  textColor: '#667085',
  fontSize: 12,
  axis: { ticks: { text: { fill: '#667085' } }, legend: { text: { fill: '#344054', fontWeight: 600 } } },
  grid: { line: { stroke: '#eef1f6' } },
  tooltip: { container: { background: '#101828', color: '#fff', borderRadius: 8, fontSize: 12, padding: '8px 10px', boxShadow: '0 8px 24px rgba(16,24,40,.25)' } },
  legends: { text: { fill: '#344054' } },
};

export const SERIES = ['#1f75e0', '#f79009', '#12b76a', '#7a5af8', '#f04438', '#06aed4'];
// The organization theme lives on <html data-org>, so the current accent is always readable here.
export const accentColor = () => getComputedStyle(document.documentElement).getPropertyValue('--accent').trim() || '#1f75e0';

const hasData = (rows) => Array.isArray(rows) && rows.length > 0;
const Box = ({ height = 320, children }) => <div style={{ height }}>{children}</div>;

export function BarChart({ data, keys, indexBy, colors = SERIES, onClick, height, valueFormat, groupMode = 'grouped', rotate = 0 }) {
  if (!hasData(data)) return <Empty title="No data for this selection" />;
  return (
    <Box height={height}>
      <ResponsiveBar data={data} keys={keys} indexBy={indexBy} groupMode={groupMode} theme={theme} colors={colors}
        margin={{ top: 16, right: 16, bottom: 56, left: 52 }} padding={0.3} innerPadding={2} borderRadius={4}
        enableLabel={false} valueFormat={valueFormat} onClick={onClick} animate
        axisBottom={{ tickRotation: rotate, tickSize: 0, tickPadding: 8 }} axisLeft={{ tickSize: 0, tickPadding: 8 }}
        legends={keys.length > 1 ? [{ dataFrom: 'keys', anchor: 'bottom', direction: 'row', translateY: 52, itemWidth: 90, itemHeight: 16, symbolSize: 10, symbolShape: 'circle' }] : []}
        onMouseEnter={(_, e) => { if (onClick) e.currentTarget.style.cursor = 'pointer'; }} />
    </Box>
  );
}

export function LineChart({ data, onPointClick, height, yFormat, colors = SERIES }) {
  const filled = (data || []).filter((s) => s.data.some((p) => p.y !== null));
  if (!filled.length) return <Empty title="No data for this selection" />;
  return (
    <Box height={height}>
      <ResponsiveLine data={data} theme={theme} colors={colors} margin={{ top: 16, right: 24, bottom: 76, left: 60 }}
        xScale={{ type: 'point' }} yScale={{ type: 'linear', min: 0, max: 'auto' }} yFormat={yFormat}
        curve="monotoneX" lineWidth={2.5} pointSize={7} pointBorderWidth={2} pointBorderColor={{ from: 'serieColor' }}
        pointColor="#fff" useMesh enableGridX={false} axisTop={null} axisRight={null}
        axisBottom={{ tickRotation: -60, tickSize: 0, tickPadding: 8 }} axisLeft={{ tickSize: 0, tickPadding: 8 }}
        onClick={onPointClick}
        legends={[{ anchor: 'bottom', direction: 'row', translateY: 76, itemWidth: 70, itemHeight: 16, symbolSize: 10, symbolShape: 'circle' }]} />
    </Box>
  );
}

export function Donut({ data, colors, height = 200, onClick }) {
  const total = (data || []).reduce((sum, d) => sum + d.value, 0);
  if (!total) return <Empty title="No data" />;
  return (
    <Box height={height}>
      <ResponsivePie data={data} theme={theme} colors={colors || [accentColor(), '#e6e9f0']} innerRadius={0.7} padAngle={1.5}
        cornerRadius={4} activeOuterRadiusOffset={6} enableArcLabels={false} enableArcLinkLabels={false}
        margin={{ top: 8, right: 8, bottom: 8, left: 8 }} onClick={onClick} />
    </Box>
  );
}

export function Calendar({ data, from, to, onClick, height = 220 }) {
  if (!hasData(data)) return <Empty title="No activity in this year" />;
  const color = accentColor();
  return (
    <Box height={height}>
      <ResponsiveCalendar data={data} from={from} to={to} theme={theme} emptyColor="#f2f4f7"
        colors={[`${color}33`, `${color}77`, `${color}bb`, color]} margin={{ top: 24, right: 8, bottom: 0, left: 24 }}
        yearSpacing={40} monthBorderColor="#fff" dayBorderWidth={2} dayBorderColor="#fff" onClick={onClick}
        tooltip={({ day, value }) => <div style={{ ...theme.tooltip.container }}>{day}: <b>{value}</b></div>} />
    </Box>
  );
}
